/**
 * Reverse Engineering for FGDB, Shapefile...
 */

export namespace RE {

	export interface ShapefileOptions {
		previewNum?: number; // The number of rows to read from dbf
	}

	export class Fgdb {
		// Reverse Engineering fgdb .gdbtablx file
		// .gdbtablx files contain the start positions of rows in the associated .gdbtable file.
			// Read .gdbtablx using FileReader
			static readGdbTablx(file): Promise<{rowStartPos: number[]}> {
				const promise: Promise<any> = new Promise((resolve, reject) => {
					const reader: FileReader = new FileReader();
					reader.onload = () => {
						const results = this.interpretGdbTablx(reader.result);
						resolve(results);
					};
					reader.readAsArrayBuffer(file);
				});
				return promise;
			}
			// Interpret the ArrayBuffer
			// numOfRows: the first x(number) of rows' position will be returned. If null, all rows positions will be returned
			static interpretGdbTablx(results: ArrayBuffer, numOfRows?): {rowStartPos: number[]} {
				const dataView = new DataView(results);
				// Header Section
					// 0 - 3 (fixed): 3 0 0 0
					// 4 - 7 (int32): number of blocks of offsets for 1024 features that are effectively present in that file.
					const p1024BlocksNum = dataView.getInt32(4, true);
					// 8 - 11 (int32): number of rows
					const rowNum = dataView.getInt32(8, true);
					// 12 - 15 (int32): number of bytes to encode each feature offset.
					// Must be 4 (.gdbtable up to 4GB), 5 (.gdbtable up to 1TB) or 6 (.gdbtable up to 256TB)
					const sizeOffset = dataView.getInt32(12, true);
				// Offset Section
				// Total bytes: sizeOffset * rowNum
					const rowPositions = [];
					let extraByte, calcSecondPart;
					switch (sizeOffset) {
						case 4: // int32
							extraByte = 0;
							calcSecondPart = (p) => 0;
							break;
						case 5: // int40 // made of a int32 with the 32 least significant bits followed by a 4th byte with the 8 most significant bits
							extraByte = 1;
							calcSecondPart = (p) => dataView.getInt8(p);
							break;
						case 6: // int48 // similar as int40
							extraByte = 2;
							calcSecondPart = (p) => dataView.getInt16(p);
					}
					numOfRows = numOfRows || rowNum;
					for (let i = 16; i <= 16 + sizeOffset * (numOfRows - 1); i += 4 + extraByte) {
						const firstPart = dataView.getInt32(i, true);
						const secondPart = calcSecondPart(i + 4);
						rowPositions.push(firstPart + secondPart);
					}
					return {
						rowStartPos: rowPositions
					};
			}

		// // Reverse Engineering fgdb .gdbtable file
		// 	// Read .gdbtable using FileReader
		// 	static readGdbTableFile(file, rowStartPos, isReadingRows?): Promise<{name: string, fields: any[], rows?: any[]}> {
		// 		return new Promise((resolve, reject) => {
		// 			const reader: FileReader = new FileReader();
		// 			reader.onload = () => {
		// 				const results: any = this.interpretGdbTable(reader.result, rowStartPos, isReadingRows);
		// 				// Add file name to results
		// 				results.name = file.name;
		// 				resolve(results);

		// 			};
		// 			reader.readAsArrayBuffer(file);
		// 		});
		// 	}
			// Interpret the ArrayBuffer
			// Param numOfRows: number of rows to read from the file
			static interpretGdbTable(results: ArrayBuffer, rowStartPos?, numOfRows?: number | true): {fields: any[], geomType, rows?: any[], hasMeasure?, hasZ?} {
				const dataView = new DataView(results);
				const offset = dataView.byteOffset;
				// Header Section
					// 0 - 3 (fixed): 3 0 0 0
					// 4 - 7 (int32): number of rows
					const rowNum = dataView.getInt32(4 + offset, true);
					// 8 - 11, 12 - 15, 16 - 19, 20 - 23
					// 24 - 27 (int32): file size in bytes
					const fileSize = dataView.getInt32(24 + offset, true);
					// 28 - 31
					// 32 - 39: offset in bytes at which the field description section begins
					const decsBeginByte = dataView.getInt32(32 + offset, true); // TODO: might be problem. should be int64 from byte 32 to 40
				// Field Description Section
				// Starts at decsBeginByte
					// 0 - 3 (int32): size of header (???) in bytes (this field excluded)
					// 4 - 7: version of the file.  3: FGDB 9.X files, 4: FGDB 10.X files
					const fileVersion = dataView.getInt32(4 + decsBeginByte, true);
					console.log('FGDB version: ' + (fileVersion === 4 ? 10 : 9));
					// 8: layer geometry type. 0 = none, 1 = point, 2 = multipoint, 3= (multi)polyline, 4 = (multi)polygon, 9=multipatch
					const geomTypeCode = dataView.getInt8(8 + decsBeginByte);
					let geomType;
					switch (geomTypeCode) {
						case 1:
							geomType = 'Point';
							break;
						case 2:
							geomType = 'MultiPoint';
							break;
						case 3:
							geomType = 'PolyLine'; // TODO: 'MultiPolyLine' ???????????
							break;
						case 4:
							geomType = 'Polygon'; // TODO: 'MultiPolygon' ????
							break;
						case 9:
							geomType = 'MultiPatch';
					}
					// 9 10 11: unknow
					// 12 - 13: number of fields (attributes fields + geometry field + implicit OBJECTID field)
					const fieldNum = dataView.getInt16(decsBeginByte + 12, true);
					// Fields
					const fieldList = [];
					// Start at 14
					const fieldStartPosition = decsBeginByte + 14;
					// Current field start position
					let currFieldStartPosition: number = decsBeginByte + 14;
					// REPEAT FOR EACH FIELD:
					for (let fieldCount = 1; fieldCount <= fieldNum; fieldCount++) {
						// Per Field
						// 0: number of UTF-16 characters (not bytes) of the name of the field
						const nameUtf16Num = dataView.getInt8(currFieldStartPosition);
						const nameNumOfBytes = nameUtf16Num * 2;
						const fieldNameStartPosition = currFieldStartPosition + 1;
						let fieldName = '';
						for (let count = 0; count < nameUtf16Num; count++) {
							const charCode = dataView.getInt16(fieldNameStartPosition + count * 2, true);
							fieldName = charCode === 0 ? fieldName : fieldName + String.fromCharCode(charCode);
						}
						// field name alias
						const nameAliasPosition = fieldNameStartPosition + nameNumOfBytes;
						const nameAliasUtf16Num = dataView.getInt8(nameAliasPosition);
						const nameAliasNumOfBytes = nameAliasUtf16Num * 2;
						// filed type
						const fieldTypePosition = nameAliasPosition + nameAliasNumOfBytes + 1;
						const fieldType = dataView.getInt8(fieldTypePosition);
						let fieldNumOfBytes: number;
						let fieldFlag = null;
						const field: any = {};
						switch (fieldType) {
							case 4:
								fieldNumOfBytes = 6;
								// 0 - 3: maximum length of string
								field.maxLength = dataView.getInt32(fieldTypePosition + 1, true);
								// 4: flag
								fieldFlag = dataView.getInt8(fieldTypePosition + 5);
								// 5:  length of default value in byte if (flag&4) != 0 followed by ldf bytes
								// 2:  length of default value in byte if (flag&4) != 0 followed by ldf bytes
								if ((fieldFlag & 4) !== 0) {
									field.idf = dataView.getUint8(fieldTypePosition + 6);
									fieldNumOfBytes += field.idf;
								}
								break;
							case 6: // objectid
								fieldNumOfBytes = 2;
								// 0: unknow role = 4    1: unknow role = 2
								field.isObjectId = true;
								break;
							case 7: // geometry
								// 0: unknown role = 0
								// 1: flag (6 or 7)
								fieldFlag = dataView.getInt8(fieldTypePosition + 2);
								// 2 - 3: length in bytes of WKT string
								const wktNumOfBytes = dataView.getInt16(fieldTypePosition + 3, true);
								const fixedNumOfBytes = wktNumOfBytes + 117;
								const extraBytesStartPos = fieldTypePosition + fixedNumOfBytes + 1;
								const secondValues = [1, 2, 3];
								let currentPos = extraBytesStartPos;
								for (let i = extraBytesStartPos; i < dataView.byteLength; i += 8) {
									currentPos = i;
									if (dataView.getInt8(currentPos) === 0) {
										const secondValue = dataView.getInt8(currentPos + 1);
										if (secondValues.indexOf(secondValue) !== -1) {
											if (dataView.getInt8(currentPos + 2) === 0) {
												if (dataView.getInt8(currentPos + 3) === 0) {
													if (dataView.getInt8(currentPos + 4) === 0) {
														// end
														fieldNumOfBytes = i - fieldTypePosition + 4 + secondValue * 8;
														break;
													} else {
														continue;
													}
												} else {
													continue;
												}
											} else {
												continue;
											}
										} else {
											continue;
										}
									} else {
										continue;
									}
								}
								field.isGeometry = true;
								break;
							case 8: // binary
								fieldNumOfBytes = 2;
								// 0: unknown role
								// 1: flag
								fieldFlag = dataView.getInt8(fieldTypePosition + 2);
								break;
							case 9:
								fieldNumOfBytes = 0;
								fieldFlag = dataView.getInt8(fieldTypePosition + 2);
								break;
							case 10:
							case 11:
								fieldNumOfBytes = 2;
								// 0: width = 38
								field.width = dataView.getInt8(fieldTypePosition + 1);
								// 1: flag
								fieldFlag = dataView.getInt8(fieldTypePosition + 2);
								break;
							case 12:
								fieldNumOfBytes = 2;
								// 0: width = 0
								field.width = dataView.getInt8(fieldTypePosition + 1);
								// 1: flag
								fieldFlag = dataView.getInt8(fieldTypePosition + 2);
								break;
							default:
								fieldNumOfBytes = 3;
								// 0: width in bytes (eg. 2 for int16, 4 for int32, 4 for float32, 8 for float64, 8 for datetime)
								field.width = dataView.getInt8(fieldTypePosition + 1);
								// 1: flag
								fieldFlag = dataView.getInt8(fieldTypePosition + 2);
								// 2:  length of default value in byte if (flag&4) != 0 followed by ldf bytes
								if ((fieldFlag & 4) !== 0) {
									field.idf = dataView.getInt8(fieldTypePosition + 3);
									fieldNumOfBytes += field.idf;
								}
						}
						field.name = fieldName;
						field.type = fieldType;
						field.flag = fieldFlag;
						//  If lsb (least-significant-bit, I guess..) is 1, the field can be null.
						field.nullable = fieldFlag & 1;
						fieldList.push(field);
						currFieldStartPosition = fieldTypePosition + fieldNumOfBytes + 1;
					}

				if (!rowStartPos || !numOfRows || numOfRows <= 0) {
					return {
						fields: fieldList,
						geomType: geomType
					};
				}
				// Row Section
					const rows = [];
					let hasMeasure: boolean = null; // Layer has measure in geometry or not
					let hasZ: boolean = null; // Layer has z in geometry or not
					// Number of nullable fields
					let nullableFieldsNum = 0;
					fieldList.forEach((f) => {
						if (f.nullable) {
							nullableFieldsNum++;
						}
					});
					const flagsBytesNum = Math.ceil(nullableFieldsNum / 8);
					// Existing rows' start positions
					let rowStartPosE = rowStartPos.filter((n) => n !== 0 );
					if (numOfRows !== true && numOfRows < rowStartPosE.length) {
						rowStartPosE = rowStartPosE.slice(0, numOfRows);
					}
					rowStartPosE.forEach((startPos, posIdx) => {
						const row: any = {};
						let currPos = startPos;
						// Row blob length (this field excluded)
						const rowLength = dataView.getInt32(startPos, true);
						currPos += 4;
						// flags bytes
						// Calculate flags
						const nullFlags = [];
						for (let i = 0; i < flagsBytesNum; i++) {
							let flagsStr = dataView.getUint8(currPos + i).toString(2);
							flagsStr = '00000000'.substr(flagsStr.length) + flagsStr;
							for (let j = 7; j >= 0; j--) {
								nullFlags.push(Number(flagsStr[j]));
							}
						}
						currPos += flagsBytesNum;
						// Nullable count
						let nCount = 0;
						fieldList.forEach((f, idx) => {
							// // If position already exceeds current row
							// if (rowStartPosE[posIdx + 1] && currPos >= rowStartPosE[posIdx + 1]) {
							// 	return;
							// }
							// If field is null
							if (f.nullable) {
								if (nullFlags[nCount]) {
									nCount++;
									return;
								}
								nCount++;
							}
							switch (f.type) {
								case 4: // String
									const strLength = dataView.getUint8(currPos);
									let str = '';
									for (let i = currPos + 1; i <= currPos + strLength; i++) {
										str += String.fromCharCode(dataView.getInt8(i));
									}
									row[f.name] = str;
									currPos += 1 + strLength;
									break;
								case 6: // ObjectId
									// There's no explicit data for OBJECTID and no reserved flag bit for it.
									break;
								case 7: // Geometry
									// if (hasMeasure !== null) {
									// 	break;
									// }
									// Calculate geomBlobLenth and hasMeasure
									// The length of geom blob; The length of the field that stores the geom blob;
									let geomBlobLenth, geomBlobLenthFieldLength;
									// Reference to the M Component Part in the document
									let geomTypePos;
									const geomTypeBytes = []; // geom_type bytes in binary
									for (let i = 0; i < 8; i++) {
										const bitStr = dataView.getUint8(currPos + i).toString(2);
										const bitStrFull = '00000000'.substr(bitStr.length) + bitStr;
										geomBlobLenth = geomBlobLenth | ((dataView.getUint8(currPos + i) & 0x7F) << (7 * i));
										if (bitStrFull[0] === '0') {
											geomTypePos = currPos + i + 1;
											geomBlobLenthFieldLength = i + 1;
											break;
										}
									}
									for (let i = 0; i < 8; i++) {
										const geomByte = dataView.getUint8(geomTypePos + i);
										geomTypeBytes.push(geomByte);
										const bitStr = geomByte.toString(2);
										const bitStrFull = '00000000'.substr(bitStr.length) + bitStr;
										if (bitStrFull[0] === '0') {
											break;
										}
									}
									let geomTypeVar = geomTypeBytes[0] & 0x7F;
									// console.log('geomTypeVarrrrrrrrrrr: ' + geomTypeVar);
									for (let i = 1; i < geomTypeBytes.length; i++) {
										geomTypeVar = geomTypeVar | ((geomTypeBytes[i] & 0x7F) << (i * 7));
									}
									if ((geomTypeVar & 0x40000000) != 0) {
										hasMeasure = true;
									} else {
										hasMeasure = false;
									}
									if ((geomTypeVar & 0x80000000) != 0) {
										hasZ = true;
									} else {
										hasZ = false;
									}
									currPos += geomBlobLenth + geomBlobLenthFieldLength;
									break;
								case 8: // Binary
									// TODO
									break;
								case 9: // Raster
									currPos += 4;
									break;
								case 10:
								case 11: // UUID
									currPos += 16;
									break;
								case 12: // XML
									const xml = this.checkXmlEnds(dataView, currPos, rowStartPosE[posIdx + 1]);
									currPos += xml.length;
									row[f.name] = xml;
									break;
								default:
								// Date
								try {
									if (f.type === 5) { // datetime
										const baseDate = new Date('December 30, 1899 00:00 UTC');
										baseDate.setDate(baseDate.getDate() + dataView.getFloat64(currPos, true));
										if (dataView.getFloat64(currPos, true) && !!baseDate.getTime()) {
											row[f.name] = baseDate.toISOString();
										}
									}
								} catch (err) {
									delete row[f.name];
								}
									currPos += f.width;
							}
						});
						rows.push(row);
					});
					if (hasMeasure && (geomType === 'PolyLine' || geomType === 'Point')) {
						geomType = geomType + 'M';
					}
					return {
					fields: fieldList,
					rows: rows,
					geomType: geomType,
					hasMeasure: hasMeasure, // TODO: Remove??
					hasZ: hasZ
				};
			}
			// Check when xml ends in dataView
			static checkXmlEnds(dataView, fromPos, endPos) {
				let xml = '';
				let startTag = '';
				let startTagPos;
				// Start Tag Position
				for (let i = fromPos; i <= endPos; i++) {
					if (dataView.getInt8(i) === 60) { // 60: '<'
						startTagPos = i;
						break;
					}
				}
				// Start Tag
				const breakCodes = [62, 32, 9]; // 62: '>', 32: ' ', 09: '	' (tab)
				const startTagCodes = [];
				let startTagEndPos;
				for (let i = startTagPos; i <= endPos; i++) {
					const code = dataView.getInt8(i);
					startTag += String.fromCharCode(code);
					if (breakCodes.indexOf(code) !== -1) {
						xml = startTag;
						startTagEndPos = i;
						break;
					} else {
						startTagCodes.push(code);
					}
				}
				// Check end Tag
				let endTagStartPos, endTagEndPos;
				for (let i = startTagEndPos; i <= endPos; i++) {
					const code = dataView.getInt8(i);
					xml += String.fromCharCode(code);
					if (code === 60 && dataView.getInt8(i + 1) === 47) { // 60: '<', 47: '/'
						endTagStartPos = i;
						let isMatch = true;
						for (let j = 0; j < startTagCodes.length; j++) {
							if (dataView.getInt8(i + 2 + j) !== startTagCodes[j]) {
								isMatch = false;
								break;
							}
						}
						if (isMatch && dataView.getInt8(i + startTagCodes.length) === 62) {
							// Find End Tag!
							endTagEndPos = i + startTagCodes.length;
							break;
						}
					}
				}
				// Add end tag to xml
				for (let i = endTagStartPos + 1; i <= endTagEndPos; i++) {
					xml += String.fromCharCode(dataView.getInt8(i));
				}
				return xml;
			}
	}

	export class Shapefile {
		// Reverse Engineering shp file
			// Read shapefile's shp using FileReader
			// https://www.esri.com/library/whitepapers/pdfs/shapefile.pdf
			static readShp(file): Promise<{type: string}> {
				return new Promise((resolve, reject) => {
					const readerShp: FileReader = new FileReader();
					readerShp.onload = () => {
						const results = this.interpretShp(readerShp.result);
						resolve(results);
					};
					readerShp.readAsArrayBuffer(file);
				});
			}
			// Interpret the ArrayBuffer
			static interpretShp(results: ArrayBuffer): {type: string} {
				const dataView = new DataView(results);
				const offset = dataView.byteOffset;
				const shapeTypeCode = dataView.getInt8(32);
				let featureType;
				switch (shapeTypeCode) {
					case 1: // Point
						featureType = 'Point';
						break;
					case 8: // MultiPoint
						featureType = 'MultiPoint';
						break;
					case 11: // PointZ
						featureType = 'PointZ';
						break;
					case 18: // MultiPointZ
						featureType = 'MultiPointZ';
						break;
					case 21: // PointM
						featureType = 'PointM';
						break;
					case 28: // MultiPointM
						featureType = 'MultiPointM';
						break;
					case 3: // PolyLine
						featureType = 'PolyLine';
						break;
					case 13: // PolyLineZ
						const recordContentStartPos = 108; // main file header: 100 bytes. record header: 8 bytes
						// The following algorithm is from esri shapefile documentation
						const numParts = dataView.getInt8(recordContentStartPos + 36);
						const numPoints = dataView.getInt8(recordContentStartPos + 40);
						const byteX = recordContentStartPos + 44 + (4 * numParts);
						const byteY = byteX + (16 * numPoints);
						const byteZ = byteY + 16 + (8 * numPoints);
						const maxMeasPos = byteZ + 8;
						const maxMeas = dataView.getFloat64(maxMeasPos);
						const lastMeasPos = byteZ + 8 + 8 * numPoints;
						const lastMeas = dataView.getFloat64(lastMeasPos);
						if (maxMeas === lastMeas) { // polylineZ with measure
							featureType = 'PolyLineZM';
						} else { // polylineZ without measure
							featureType = 'PolyLineZ';
						}
						break;
					case 5: // Polygon
						featureType = 'Polygon';
						break;
					case 23: // PolyLineM
						featureType = 'PolyLineM';
						break;
				}
				return {
					type: featureType
				};
			}
		// Reverse Engineering dbf file
			// Read shapefile's DBF using FileReader
			static readDbf(file, options?: ShapefileOptions): Promise<{headers: any[], rows: any[]}> {
				return new Promise((resolve, reject) => {
					const reader: FileReader = new FileReader();
					reader.onload = () => {
						if (reader.result) {
							const dbfResults = this.interpretDbf(reader.result, options);
							resolve(dbfResults);
						} else {
							resolve({
								headers: [],
								rows: []
							});
						}
					};
					reader.readAsArrayBuffer(file);
				});
			}
			// Interpret the ArrayBuffer
			// Param options: previewNum: the number of rows that needed to read from dbf
			static interpretDbf(results: ArrayBuffer, options?: ShapefileOptions): {headers: {name: string; type: string; length: number}[], rows: any[]} {
				options = options || {};
				const dataView = new DataView(results);
				const offset = dataView.byteOffset;
				// http://www.dbase.com/Knowledgebase/INT/db7_file_fmt.htm
				// http://www.dbfree.org/webdocs/1-documentation/b-dbf_header_specifications.htm

				// 8-9: position of First data record
				const firstDataBytePosition = dataView.getInt16(offset + 8, true) - 1; // 8 is the byte position to store the first data position
				// 32 - n: Field subrecords. The number of fields determines the number of field subrecords
				const firstHeaderFieldBytePosition = 32 + offset; // 32 is the byte position to store the first header field.
				const headerNum = (firstDataBytePosition - firstHeaderFieldBytePosition ) / 32; // 32 is the total bytes for one field.
				const headerNameList = [];
				for (let n = 0; n < headerNum; n++) {
					let fieldName = '';
					const startPosition = firstHeaderFieldBytePosition + 32 * n;
					// From startPosition 0 - 10: field name
					for (let count = 0; count < 10; count++) {
						const charCode = dataView.getInt8(startPosition + count);
						fieldName = charCode === 0 ? fieldName : fieldName + String.fromCharCode(charCode);
					}
					// From startPosition 11: Field Type
					const fieldTypePosition = startPosition + 11;
					const fieldType = String.fromCharCode(dataView.getInt8(fieldTypePosition));
					// // From startPosition 12 - 15: Displacement of field in record
					// const displacementInRec = dataView.getInt32(startPosition + 12);
					// From startPosition 16: field length in bytes
					const fieldLength = dataView.getUint8(startPosition + 16);
					// // From startPosition 17: number of decimal places
					// const numOfDecimalPlace = dataView.getInt8(startPosition + 17);
					// // From startPosition 18: field flag
					// const fieldFlag = dataView.getInt8(startPosition + 18);
					// // From startPosition 19 - 22: Value of autoincrement Next value
					// const autoincrementNextVal = dataView.getInt32(startPosition + 19);
					// // From startPosition 23: Value of autoincrement Step value
					// const autoincrementStepVal = dataView.getInt8(startPosition + 23);
					headerNameList.push({
						name: fieldName.trim(),
						type: fieldType,
						length: fieldLength
					});
				}

				// Records starting position
				const recordStartPos = firstHeaderFieldBytePosition + 32 * headerNum + 1;
				let currPos = recordStartPos;
				const rows = [];
				// Default options.previewNum: 15
				const numOfRows = (!options.previewNum && options.previewNum !== 0) ? 15 : options.previewNum;
				const recordLength = headerNameList.reduce((acc, i) => {
					return acc + i.length;
				}, 0);
				for (let i = 0; i < numOfRows; i++) {
					if ((currPos + recordLength * i) > dataView.byteLength) { break; }
					const row = {};
					currPos++; // TODO: this is hard coded (offset byte as 1) based on some sample shapefile; // Not able to find the offset of row start position yet
					headerNameList.forEach((h) => {
						if (h.length <= 0) { return; }
						let str = '';
						for (let j = 0; j < h.length; j++) {
							str += String.fromCharCode(dataView.getUint8(currPos + j));
						}
						currPos += h.length;
						switch (h.type) {
							case 'N': // Numeric
							case 'F': // Float
								row[h.name] = Number(str);
								break;
							case 'C': // Character
								row[h.name] = str.trim();
								break;
							default:
								row[h.name] = str.trim();
						}
					});
					rows.push(row);
				}

				return {
					headers: headerNameList,
					rows: rows
				};
			}
		// Read shapefile's Prj using FileReader
			static readPrj(file): Promise<{epsg: number}> {
				return new Promise((resolve, reject) => {
					const reader: FileReader = new FileReader();
					reader.onload = () => {
						const results = this.interpretPrj(reader.result);
						resolve(results);
					};
					reader.readAsText(file);
				});
			}
			static interpretPrj(results: string): {epsg: number} {
				const wktPrj = results;
				const substring = wktPrj.split('EPSG')[1];
				if (!substring) {
					return null;
				}
				// Find the number after 'EPSG'
				const epsg = substring.trim().replace( /(^.+\D)(\d+)(\D.+$)/i, '$2');
				return {
					epsg: Number(epsg)
				};
			}
			static getEpsgFromWktPrj(wktPrj: string) {

			}

	}
}



// WEBPACK FOOTER //
// ./src/assets/TS/FileReverseEng.ts